import React, { Component, ErrorInfo, ReactNode } from "react";
import Text from 'antd/lib/typography/Text';
import MinusOutlined from '@ant-design/icons/MinusOutlined';
import { appInsights } from "../../Logging/appInsights";

interface Props {
    env?: string;
    children?: ReactNode;
}

interface State {
    hasError: boolean;
    errObj?: Error;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false
    };

    public getComponentTypeName(children: ReactNode): string {

        if (React.isValidElement(children)) {

            //use 'item type' which seems to match our component names
            const itemType = children.props?.itemType;
            if (itemType) {
                return itemType;
            }

            //otherwise fall back to hopefully something meaningful
            const elementType = children.type;
            // Check if the type is a function or class component (which has a name)
            // This will exclude strings representing HTML tags
            if (typeof elementType === "function") {
                // @ts-expect-error if the properties don't exist just fallback to 'unknown'
                return elementType.displayName || elementType.name || "Unknown";
            }
        }
        return "Unknown";
    }

    public static getDerivedStateFromError(_: Error): State {
        // Update state so the next render will show the fallback UI.
        console.error(_);
        return { hasError: true, errObj: _ };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        const componentType = this.getComponentTypeName(this?.props?.children);
        console.error("Uncaught error:", error, errorInfo, componentType);

        appInsights?.trackException({ error: new Error(`Error Boundary error`), severityLevel: 4 }, { url: window.location.href, componentType: componentType, error: error, errorInfo: errorInfo });
    }

    public render() {
        if (this.state.hasError) {
            const componentType = this.getComponentTypeName(this.props.children);

            const showError = this.props.env !== "prod";

            const errMsg = `[There was an error rendering component type: '${componentType}' with error: ${JSON.stringify(this?.state?.errObj)}]`;

            return (
                showError ?
                    <div style={{ paddingTop:"20px", paddingBottom:"20px" }} >
                        <Text style={{ color: "red" }} >
                            {errMsg}
                        </Text>
                    </div>
                    : <div style={{ width: "100%", display: "flex", justifyContent: "center" }} >
                        <MinusOutlined data-err={errMsg} style={{ color: "#41C0F0" }} />
                    </div>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;